import { useLayoutEffect, useState } from 'react';
import { useSelector } from '@legendapp/state/react';
import { getRightPanelLayout } from '../constants/breakpoints';
import { platform } from '../lib/native';
import { AI_PANEL_MIN_WIDTH, ui$ } from '../store/ui$';

const readLayout = (panelWidth, sidebarCollapsed) => getRightPanelLayout({
  viewportWidth: typeof window === 'undefined' ? 0 : window.innerWidth,
  panelWidth,
  sidebarExpanded: !sidebarCollapsed,
  isNative: platform.isNative,
});

/**
 * Derives right-panel placement from live shell geometry: `docked`, `floating`,
 * or — on the phone shell — `page`. The mode is transient layout state, so it
 * stays local instead of being persisted in Legend State.
 */
export const useRightPanelLayout = (panelWidth) => {
  const sidebarCollapsed = useSelector(() => ui$.sidebarCollapsed.get());
  const [layout, setLayout] = useState(() => readLayout(panelWidth, sidebarCollapsed));

  useLayoutEffect(() => {
    const updateLayout = () => setLayout(readLayout(panelWidth, sidebarCollapsed));

    updateLayout();
    window.addEventListener('resize', updateLayout);
    return () => window.removeEventListener('resize', updateLayout);
  }, [panelWidth, sidebarCollapsed]);

  return layout;
};

/**
 * True when the AI surface must render as a phone page rather than a panel.
 *
 * Every AI manifest asks this instead of testing a width of its own, so the
 * three surfaces (article, library, deck) cannot disagree about which form
 * they are in.
 */
export const useRightPanelIsPage = () => {
  const panelWidth = useSelector(() => ui$.aiPanelWidth.get()) || AI_PANEL_MIN_WIDTH;
  return useRightPanelLayout(panelWidth) === 'page';
};

export default useRightPanelLayout;
